import {
    AutoCompleteInputFieldV2,
    Building,
    Bus,
    Flight,
    getFlagEmojiFromCountryCode,
    Globe,
    Hotel,
    Internet,
    ItemOptions,
    Train,
    Typography,
} from '@eventr/common-storybook'
import { AutocompleteRenderGroupParams, Stack } from '@mui/material'
import { FC, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useShallow } from 'zustand/react/shallow'
import { bs } from '../../../state/bound.store'
import { useEphemeralStore } from '../../../state/useEphemeralStore'
import { capitalizeFirstLetter } from '../../../utils'
import { editItineraryFieldsTranslation } from '../../../i18n/translateHelpers'

interface DestinationFieldProps {}

export const DestinationField: FC<DestinationFieldProps> = () => {
    const { getDestinationOptions, destination, setDestination } =
        useEphemeralStore(
            useShallow((s) => ({
                getDestinationOptions: s.getDestinationOptions,
                destination: s.destination,
                setDestination: s.setDestination,
            }))
        )

    const [loading, setLoading] = useState(false)
    const [options, setOptions] = useState<ItemOptions[]>([])

    const onOpen = async (inputString: string) => {
        const destinations = await getDestinationOptions(
            inputString ?? '',
            bs().getEventInfoV2()?.data
        )
        setOptions(destinations)
        setLoading(false)
    }

    const onInputChange = async (value: string) => {
        // continue if its empty so this mimics the default nearest search
        if (value.length < 3 && value.length !== 0) return
        // if (event.nativeEvent.inputType === 'deleteContentBackward') return

        const destinations = await getDestinationOptions(
            value,
            bs().getEventInfoV2()?.data
        )
        setOptions(destinations)
        setLoading(false)
    }
    const debouncedOnInputChange = useDebouncedCallback((value: string) => {
        onInputChange(value)
    }, 1000)

    const GroupHeader = ({ children }: AutocompleteRenderGroupParams) => {
        return (
            <Typography
                style={{ paddingLeft: '18px' }}
                variant="bodySubtextBold"
            >
                {children}
            </Typography>
        )
    }
    const GroupItems = ({ children }: AutocompleteRenderGroupParams) => {
        return (
            <ul style={{ listStyle: 'none', paddingLeft: '18px' }}>
                {children}
            </ul>
        )
    }
    const OptionSubText = ({ option }: { option: ItemOptions }) => {
        if (!option) return null
        if (option.obj.grType === 'Regions') return null
        return (
            <Typography variant="bodySubtext">
                {getFlagEmojiFromCountryCode(option.obj.countryCode)}{' '}
                {option.obj.address}
            </Typography>
        )
    }
    const OptionIcon = ({ option }: { option: ItemOptions }) => {
        switch (option.obj.type) {
            case 'Point of Interest':
            case 'City':
            case 'Neighborhood':
                return <Building />
            case 'Province (State)':
            case 'Multi-Region (within a country)':
            case 'Multi-City (Vicinity)':
                return <Internet />
            case 'Airport':
                return <Flight />
            case 'Bus Station':
            case 'Street':
                return <Bus />
            case 'Multi-Railway Station':
            case 'Railway Station':
            case 'Subway (Entrace)':
                return <Train />
            case 'Continent':
            case 'Country':
                return <Globe />
            case 'Hotel':
            default:
                return <Hotel />
        }
    }

    return (
        <AutoCompleteInputFieldV2
            fieldLabel={editItineraryFieldsTranslation('Location')}
            onChange={(item) => {
                if (item) {
                    setDestination(item)
                }
            }}
            /// Group by also needs list to be ordered.... mui sucks.
            groupBy={(option) => {
                return capitalizeFirstLetter(option?.obj?.grType)
            }}
            onOpen={(e) => {
                if (!destination) {
                    setLoading(true)
                    onOpen((e.target as any).value)
                }
            }}
            onInputChange={(e, v) => {
                if (v === '') {
                    setDestination(null)
                }
                if (v.length < 3 && v.length !== 0) return
                if (e?.type === 'change') {
                    setLoading(true)
                    setDestination({
                        label: v,
                        obj: { grType: 'Regions' },
                    })
                    debouncedOnInputChange(v)
                }
            }}
            renderGroup={(params) => {
                return (
                    <li key={params.key}>
                        <GroupHeader>{params.group}</GroupHeader>
                        <GroupItems>{params.children}</GroupItems>
                    </li>
                )
            }}
            freeSolo={true as any}
            renderOption={(props, option) => {
                if (!option) return null
                return (
                    <li {...props}>
                        <Stack
                            alignItems={'center'}
                            direction={'row'}
                            spacing={'4px'}
                        >
                            <OptionIcon option={option} />
                            <Stack>
                                <Typography>{option.label}</Typography>
                                <OptionSubText option={option} />
                            </Stack>
                        </Stack>
                    </li>
                )
            }}
            onBlur={(e) => {
                const textInput = (e.target as any).value
                if (textInput.length > 0 && !destination) {
                    const region = options?.find(
                        (o) =>
                            o.obj.grType === 'Regions' &&
                            o.label.toLowerCase() === textInput.toLowerCase()
                    )
                    setDestination(region ?? null)
                }
            }}
            loading={loading}
            options={options}
            listLimit={10}
            loadingText="Loading"
            value={destination}
        />
    )
}
